// teaser-slider.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import Flickity from "flickity";

class TeaserSlider {
    constructor(elementOrSelector) {
        // Selector ODER Element akzeptieren
        this.root = typeof elementOrSelector === 'string'
            ? document.querySelector(elementOrSelector)
            : elementOrSelector;

        if (!this.root) {
            console.warn('TeaserSlider: root element not found.', elementOrSelector);
            return;
        }

        // Nur Benefits-Slider equalizen
        this.isBenefits = this.root.classList.contains('benefits');

        // Neuer Selector: .carousel
        this.carouselEl = this.root.querySelector(".carousel");
        if (!this.carouselEl) {
            console.warn('TeaserSlider: .carousel not found within root.', this.root);
            return;
        }

        // Buttons
        this.nextArrow = this.root.querySelector(".js-slider-button-next");
        this.prevArrow = this.root.querySelector(".js-slider-button-prev");

        if (!this.nextArrow || !this.prevArrow) {
            console.warn('TeaserSlider: missing prev/next arrow buttons.', this.root);
        }

        // Flickity Initialisierung
        this.flkty = new Flickity(this.carouselEl, {
            adaptiveHeight: false,
            cellAlign: "left",
            draggable: true,
            pageDots: false,
            prevNextButtons: false,
            initialIndex: 0,
            wrapAround: false
        });

        this.slideCount = 0;

        this.flkty.on("ready", () => {
            this.slideCount = this.flkty.slides.length;

            if (this.isBenefits) {
                this.equalizeHeights();
            }

            this.setCurrentSlide(this.flkty.selectedIndex);
            this.updatePassedSlides(this.flkty.selectedIndex);
            ScrollTrigger.refresh();
        });

        this.flkty.on("settle", () => ScrollTrigger.refresh());

        this.flkty.on("change", (index) => this.onSlideChange(index));

        // Button-Events
        this.prevArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.previous();
        });

        this.nextArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.next();
        });

        // Refresh nach Load
        const init = () => this.refresh();

        if (document.readyState === "complete") init();
        else window.addEventListener("load", init);

        // Bei Resize nur für Benefits neu equalizen
        window.addEventListener("resize", () => {
            if (this.isBenefits) {
                this.equalizeHeights();
            }
        });
    }

    onSlideChange(index) {
        this.setCurrentSlide(index);
        this.updatePassedSlides(index);
    }

    updatePassedSlides(activeIndex) {
        if (!this.flkty?.cells) return;

        this.flkty.cells.forEach((cell, i) => {
            const slide = cell.element;
            if (!slide) return;

            if (i < activeIndex) {
                slide.classList.add("is-passed");
            } else {
                slide.classList.remove("is-passed");
            }
        });
    }

    setCurrentSlide(index) {
        if (!this.prevArrow || !this.nextArrow) return;

        if (index === 0) {
            this.prevArrow.classList.add("is-disabled");
        } else {
            this.prevArrow.classList.remove("is-disabled");
        }

        if (index === this.slideCount - 1) {
            this.nextArrow.classList.add("is-disabled");
        } else {
            this.nextArrow.classList.remove("is-disabled");
        }
    }

    refresh() {
        if (!this.flkty) return;

        this.flkty.reloadCells();
        this.flkty.resize();
        this.flkty.reposition();
        this.slideCount = this.flkty.slides.length;

        if (this.isBenefits) {
            this.equalizeHeights();
        }

        this.setCurrentSlide(this.flkty.selectedIndex);
        this.updatePassedSlides(this.flkty.selectedIndex);
    }

    /**
     * Alle Slides auf die gleiche Höhe bringen (nur Benefits)
     */
    equalizeHeights() {
        if (!this.flkty?.cells || this.flkty.cells.length === 0) return;

        // 1. zurücksetzen
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            el.style.height = "auto";
        });

        // 2. Maximalhöhe bestimmen
        let maxHeight = 0;
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            const h = el.offsetHeight;
            if (h > maxHeight) maxHeight = h;
        });

        // 3. Maximalhöhe anwenden
        this.flkty.cells.forEach((cell) => {
            const el = cell.element;
            el.style.height = `${maxHeight}px`;
        });

        // 4. Flickity über geänderte Höhe informieren
        this.flkty.resize();
    }

    static mountAll(selector = ".js-teaser-slider") {
        document.querySelectorAll(selector).forEach((el) => new TeaserSlider(el));
    }
}

export default TeaserSlider;
